import { ensure } from "@holigral/shared/utils"
import { PHASES } from "./model"
import type { PhaseStatistics, ResponseGrid, ResultMatrix } from "./model"
import { i18n } from "@/i18n"

/**
 * A cell as write-excel-file takes it: a bare value, or a formula.
 * Typed here rather than imported so the workbook can be built — and
 * tested — without pulling the writer into the bundle.
 */
export type WorkbookCell =
  string | number | null | { readonly type: "Formula"; readonly value: string }

/**
 * Shaped as write-excel-file consumes it. The name property is `sheet`,
 * not `name` — an unrecognised key is ignored silently and the sheet
 * falls back to "Sheet1", which quietly breaks every cross-sheet
 * formula. `columns` sets column widths.
 */
export type WorkbookSheet = {
  readonly sheet: string
  readonly data: WorkbookCell[][]
  readonly columns?: { readonly width: number }[]
}

/** Raw responses occupy D…M — columns A–C carry set, vector and topic. */
const FIRST_DATA_COLUMN = 3

const columnLetter = (index: number) => String.fromCharCode(65 + index)

/**
 * No leading "=" — that is how a formula is typed into Excel, not how
 * it is stored. OOXML puts the bare expression in <f>, which is what
 * Excel itself writes (checked against Pam's own UPT_blank.xlsx: 212
 * formulas, not one of them with an "="), and write-excel-file emits
 * the value verbatim rather than normalising it.
 */
const formula = (value: string) => ({ type: "Formula" as const, value })

const RESPONSES_SHEET = "raw responses"
const CROSSTAB_SHEET = "true-true matrix"
const GRID_SHEET = "interference grid"

/**
 * Builds the workbook Pam asked for, in the layout she specified: thirty
 * rows of ten numbers, three sets deep.
 *
 * The ordering is the point. Rows n, n+10 and n+20 are the three topics
 * of one phase, so the "true-true matrix" sheet is *literally* those
 * three rows added together — written as live formulas rather than
 * baked values, so changing a raw answer shows its effect straight
 * away. That is the derivation she described, made editable.
 *
 * Labels are English regardless of the operator's locale, matching the
 * clipboard export: two workbooks from two locales have to be
 * comparable.
 */
export default function buildWorkbook(
  matrix: ResultMatrix,
  responses: ResponseGrid,
): WorkbookSheet[] {
  const t = i18n.getFixedT("en", "matrix")
  const label = (phase: string) => t(`phases.${phase}`)
  const labels = PHASES.map(label)

  const stat = (stats: PhaseStatistics | undefined, phase: string) =>
    ensure(stats, `missing statistics for ${phase}`)

  const rawResponses: WorkbookCell[][] = [
    ["set", "vector (topic phase)", "topic", ...labels],
    ...responses.rows.map((row) => [
      row.set,
      label(row.phase),
      row.topicSlug,
      ...row.values,
    ]),
  ]

  // Sheet rows are 1-based and row 1 is the header, so the three topics
  // of phase index i sit on sheet rows i+2, i+12 and i+22.
  const crosstabSheet: WorkbookCell[][] = [
    ["", ...labels],
    ...PHASES.map((phase, phaseIndex) => [
      label(phase),
      ...PHASES.map((_, column) => {
        const cell = columnLetter(FIRST_DATA_COLUMN + column)
        const refs = [2, 12, 22].map(
          (offset) => `'${RESPONSES_SHEET}'!${cell}${phaseIndex + offset}`,
        )

        return formula(refs.join("+"))
      }),
    ]),
  ]

  const grid: WorkbookCell[][] = [
    ["", ...labels, "total", "sd"],
    ...PHASES.map((phase, rowIndex) => {
      const stats = stat(matrix.rowStats[phase], phase)
      const cells = ensure(matrix.cells[rowIndex], `missing row ${rowIndex}`)

      return [
        label(phase),
        ...cells.map((cell) => cell.value),
        matrix.rowSums[phase],
        stats.sd,
      ]
    }),
    [
      "total",
      ...PHASES.map((phase) => matrix.colSums[phase]),
      PHASES.reduce((sum, phase) => sum + matrix.rowSums[phase], 0),
    ],
    ["sd", ...PHASES.map((phase) => stat(matrix.colStats[phase], phase).sd)],
    [],
    ["whole grid", "n", matrix.gridStats.n],
    ["", "mean (scaled 1…19)", matrix.gridStats.mean],
    ["", "sd", matrix.gridStats.sd],
  ]

  const about: WorkbookCell[][] = [
    ["Profiling.app — submission data export"],
    [],
    [
      RESPONSES_SHEET,
      "The 300 answers as given, raw −9…+9. Thirty rows of ten: three sets of ten topics, one topic per phase (“vector”) in each set, phases in canonical order down each set. Columns D–M are the ten option phases in that same order.",
    ],
    [
      CROSSTAB_SHEET,
      `Rows n, n+10 and n+20 of "${RESPONSES_SHEET}" are the three topics of one phase, so each cell here is those three added together — the three direct responses behind that (topic phase, option phase) pair. Live formulas: edit a raw answer and this follows.`,
    ],
    [
      GRID_SHEET,
      "What the app displays: cell = round(sqrt(rowTotal × colTotal) − grandMean), computed on the +10 scaled scores so every total stays positive. It stands in for the crosstab, which at three values a cell is too thin to read on its own. Totals and standard deviation are on the scaled values; sd is unchanged by the scaling.",
    ],
    [],
    [
      "orientation",
      "Rows are the topic phase — the phase of the question asked. Columns are the option phase — the phase of the option answered. Both axes partition the same 300 answers, so their totals share one grand total.",
    ],
  ]

  return [
    { sheet: RESPONSES_SHEET, data: rawResponses },
    { sheet: CROSSTAB_SHEET, data: crosstabSheet },
    { sheet: GRID_SHEET, data: grid },
    // The about sheet exists to be read, so give its prose column room.
    { sheet: "about", data: about, columns: [{ width: 22 }, { width: 110 }] },
  ]
}
